import {
  ButtonProps,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from "@chakra-ui/react";
import React from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";

import { ChevronRightIcon, ThreeDotsIcon } from "../../icons";

interface MenuActionBase {
  label: string;
  icon?: React.ReactNode;
  disabledText?: string;
  isDividerBefore?: boolean;
}

interface MenuActionWithAction extends MenuActionBase {
  action: (e: React.MouseEvent<HTMLElement>) => void;
  to?: never;
  subActions?: never;
}

interface MenuActionWithLink extends MenuActionBase {
  to: RouterLinkProps["to"];
  href?: never;
  action?: never;
  subActions?: never;
}

interface MenuActionWithHref extends MenuActionBase {
  href: string;
  to?: never;
  action?: never;
  subActions?: never;
}

interface MenuActionWithSubMenuActions extends MenuActionBase {
  action?: never;
  to?: never;
  href?: never;
  subActions: MenuAction[];
}

export type MenuAction =
  | MenuActionWithAction
  | MenuActionWithLink
  | MenuActionWithHref
  | MenuActionWithSubMenuActions;

type MenuActionsProps = {
  actions: MenuAction[];
  usePortal?: boolean;
};

function isLinkAction(action: MenuAction): action is MenuActionWithLink {
  return "to" in action && action.to !== undefined;
}

export function isHrefAction(action: MenuAction): action is MenuActionWithHref {
  return "href" in action && action.href !== undefined;
}

function isCallbackAction(action: MenuAction): action is MenuActionWithAction {
  return "action" in action && action.action !== undefined;
}

type RanaContextMenuProps = MenuActionsProps & ButtonProps;

export function RanaContextMenu({ actions, usePortal = true, ...rest }: RanaContextMenuProps) {
  const intl = useIntl();
  return (
    <Menu>
      <MenuButton
        minW={0}
        as={IconButton}
        variant="fourthiary"
        aria-label="Open menu"
        title={intl.formatMessage({
          id: "button.open_menu",
          defaultMessage: "Open menu",
        })}
        color="brand.sky"
        icon={<ThreeDotsIcon boxSize={6} />}
        {...rest}
      />
      <RanaContextMenuActions actions={actions} usePortal={usePortal} />
    </Menu>
  );
}

function RanaContextMenuActions({ actions, usePortal = true }: MenuActionsProps) {
  const menuList = (
    <MenuList p={0} overflow="hidden" color="brand.bg" textStyle="sm">
      {actions.map((menuAction) => {
        const { label, icon, disabledText, isDividerBefore } = menuAction;
        if (Array.isArray(menuAction.subActions) && menuAction.subActions.length) {
          return (
            <React.Fragment key={label}>
              {isDividerBefore && <MenuDivider />}
              <Menu offset={[0, 0]} placement="right-start">
                <MenuItem
                  as={MenuButton}
                  gap={2}
                  {...(disabledText ? { title: disabledText, isDisabled: true } : {})}
                  onClick={(e) => e.stopPropagation()}
                  w="full"
                >
                  <Flex alignItems="center" gap={2}>
                    {icon}
                    <Text as="span">{label}</Text>
                    <ChevronRightIcon ml="auto" />
                  </Flex>
                </MenuItem>
                <MenuList>
                  <RanaContextMenuActions actions={menuAction.subActions} usePortal={false} />
                </MenuList>
              </Menu>
            </React.Fragment>
          );
        }

        if (isLinkAction(menuAction)) {
          return (
            <React.Fragment key={label}>
              {isDividerBefore && <MenuDivider />}
              <MenuItem
                as={RouterLink}
                to={menuAction.to}
                gap={2}
                {...(disabledText ? { title: disabledText, isDisabled: true } : {})}
              >
                {icon}
                <Text as="span">{label}</Text>
              </MenuItem>
            </React.Fragment>
          );
        }

        if (isHrefAction(menuAction)) {
          return (
            <React.Fragment key={label}>
              {isDividerBefore && <MenuDivider />}
              <MenuItem
                as="a"
                href={menuAction.href}
                target="_blank"
                rel="noreferrer"
                gap={2}
                {...(disabledText ? { title: disabledText, isDisabled: true } : {})}
              >
                {icon}
                <Text as="span">{label}</Text>
              </MenuItem>
            </React.Fragment>
          );
        }

        if (!isCallbackAction(menuAction)) return null;

        return (
          <React.Fragment key={label}>
            {isDividerBefore && <MenuDivider />}
            <MenuItem
              onClick={menuAction.action}
              gap={2}
              {...(disabledText ? { title: disabledText, isDisabled: true } : {})}
            >
              {icon}
              <Text as="span">{label}</Text>
            </MenuItem>
          </React.Fragment>
        );
      })}
    </MenuList>
  );

  return usePortal ? <Portal>{menuList}</Portal> : menuList;
}

RanaContextMenu.Actions = RanaContextMenuActions;
